/**
 * The percussion under each rhythm style, as data.
 *
 * A rhythm pattern is a pianist's reading of a chart; a groove is what the rest of the band plays
 * under it — the clave and the campana under a montuno, the güira under a merengue, a kick and a
 * cross-stick under a ballad. Each pattern in chordRhythmPatterns.js names one of these by id, and
 * several share one: every salsa figure there sits on the same son clave.
 *
 * A leaf, like the patterns beside it. It knows beats and the names of the pieces struck on them,
 * and nothing about how a piece sounds — that is drumKit.js, which is the one place a synthesized
 * piece would be swapped for a recorded one.
 *
 * The one rule that differs from the patterns: **a groove is laid out from the bar lines, not from
 * each chord's start.** A piano figure restarts with every change because it is playing that chord.
 * A drummer is not playing the chord at all, and a two-bar clave that started again at a change
 * written on beat 3 would turn itself around mid-phrase — which is the one thing a clave must never
 * do. So the grooves run straight through the chart, and the changes land on top of them.
 */

import { BAR, TWO_BARS, getBlockStyle, getCycleBeats } from './chordRhythmPatterns';
import { FULL, getDropBeats, LAYER_OFF, LAYER_ON, NORMAL, SOFT, STRONG } from './chartIntensity';
import { BEAT_EPSILON } from './chordBars';

export const KICK = 'kick';
export const SNARE = 'snare';
/** A cross-stick: the stick laid across the snare and struck on the rim, the ballad's backbeat. */
export const RIM = 'rim';
export const HAT = 'hat';
export const OPEN_HAT = 'open-hat';
export const SHAKER = 'shaker';
/**
 * The scraped metal tube merengue is driven by. Cumbia's guacharaca is played the same way and is
 * close enough in sound that one piece stands in for both.
 */
export const GUIRA = 'guira';
/** An open tone on the conga. */
export const CONGA = 'conga';
/** A slap on the conga — the dry, cracked stroke a tumbao puts on 2. */
export const SLAP = 'slap';
/** The lowest of the congas, and the hand side of a merengue tambora. */
export const TUMBA = 'tumba';
/** The higher of the two bongós, the macho. */
export const BONGO = 'bongo';
/** The lower bongó, the hembra — where a martillo puts its open tones. */
export const BONGO_LOW = 'bongo-low';
export const COWBELL = 'cowbell';
/** The higher of the two timbales: a bright, ringing crack, struck in the rolls between phrases. */
export const TIMBALE = 'timbale';
export const CLAVE = 'clave';
/** The floor tom, which a worship groove keeps time on in place of the hats. */
export const TOM = 'tom';
export const RIDE = 'ride';
/** The jingles a praise band puts on 2 and 4, or runs in sixteenths over the kit. */
export const TAMBOURINE = 'tambourine';
/** Struck on the downbeat that opens a phrase, after the fill that leads into it. */
export const CRASH = 'crash';
/** A cymbal played backwards: a wash rising across the bar before a build, peaking on its downbeat. */
export const SWELL = 'swell';

export const DRUM_PIECES = [
  KICK,
  SNARE,
  RIM,
  HAT,
  OPEN_HAT,
  SHAKER,
  GUIRA,
  CONGA,
  SLAP,
  TUMBA,
  BONGO,
  BONGO_LOW,
  COWBELL,
  TIMBALE,
  CLAVE,
  TOM,
  RIDE,
  TAMBOURINE,
  CRASH,
  SWELL,
];

/**
 * A piece struck on every eighth across `beats`, the beat louder than the & between.
 *
 * Shorthand for the one kind of line a groove writes out again and again — a hat or a shaker keeping
 * time. Everything with a shape of its own is still written out a hit at a time.
 */
const eighths = (piece, onBeat, offBeat, beats = 4) =>
  Array.from({ length: beats * 2 }, (unused, index) => ({
    at: index / 2,
    piece,
    level: index % 2 === 0 ? onBeat : offBeat,
  }));

/**
 * A piece struck on every beat across `beats`, the first of each three louder — the lilt of a 6/8 bar,
 * which is counted in eighths here, felt in two groups of three.
 */
const inThrees = (piece, strong, weak, beats = 6) =>
  Array.from({ length: beats }, (unused, at) => ({ at, piece, level: at % 3 === 0 ? strong : weak }));

/**
 * The güira's stroke on every beat: one long scrape on the beat, then two short ones on the sixteenths
 * after the & — "chá-chiki". The long one is held rather than struck, because the length of a scrape
 * is most of what it sounds like.
 */
const guira = (beats = 4) =>
  Array.from({ length: beats }, (unused, beat) => [
    { at: beat, piece: GUIRA, hold: 0.4, level: 0.75 },
    { at: beat + 0.5, piece: GUIRA, level: 0.5 },
    { at: beat + 0.75, piece: GUIRA, level: 0.55 },
  ]).flat();

/** Four bars of 4/4, in beats: the length of a phrase with a fill closing it. */
const PHRASE = 16;

/**
 * A groove `length` beats long played as a four-bar phrase: a crash opening it, and a sixteenth snare
 * fill on its last beat. The hats make room for both, as a drummer's hands have to. 4/4 only, since
 * a cycle in beats doesn't follow the bar the way BAR does.
 */
const phrase = (hits, length) => [
  ...Array.from({ length: PHRASE / length }, (unused, index) =>
    hits.map((hit) => ({ ...hit, at: hit.at + index * length })),
  )
    .flat()
    .filter(({ at, piece }) => !([HAT, OPEN_HAT].includes(piece) && (at === 0 || at >= PHRASE - 1))),
  { at: 0, piece: CRASH, level: 0.9 },
  ...[0, 0.25, 0.5, 0.75].map((offset) => ({ at: PHRASE - 1 + offset, piece: SNARE, level: 0.8 })),
];

export const DRUM_GROOVES = [
  {
    id: 'pop',
    // A plain backbeat, for the plainest pattern: the kick with the bass, the snare on 2 and 4
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1, piece: SNARE, level: 0.8 },
      { at: 2, piece: KICK, level: 0.9 },
      { at: 3, piece: SNARE, level: 0.8 },
      ...eighths(HAT, 0.5, 0.35),
    ],
  },
  {
    id: 'march',
    // No hats: an oom-pah is the bass and the answer, and the kit only has to say which is which
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1, piece: SNARE, level: 0.75 },
      { at: 2, piece: KICK, level: 0.85 },
      { at: 3, piece: SNARE, level: 0.75 },
    ],
  },
  {
    id: 'vals',
    // The kick on the downbeat and a hat on every beat after it — in 3/4, which is where a vals is
    // played, the hat on 4 falls off the end of the bar and what is left is the waltz
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.85 },
      { at: 1, piece: HAT, level: 0.5 },
      { at: 2, piece: HAT, level: 0.5 },
      { at: 3, piece: HAT, level: 0.5 },
    ],
  },
  {
    id: 'cumbia',
    // The guacharaca on every beat is the style; the tumba on the & of 2 and 4 is the lift under it
    cycle: BAR,
    hits: [
      ...guira(),
      { at: 0, piece: KICK, level: 0.6 },
      { at: 1.5, piece: TUMBA, level: 0.8 },
      { at: 2, piece: KICK, level: 0.6 },
      { at: 3.5, piece: TUMBA, level: 0.8 },
    ],
  },
  {
    id: 'bolero',
    // The bongó's martillo — every eighth on the macho, the open tones on the hembra on 4 and its &
    // — over maracas. Deliberately light: a bolero is sung over, not danced on.
    cycle: BAR,
    hits: [
      { at: 0, piece: BONGO, level: 0.8 },
      { at: 0.5, piece: BONGO, level: 0.45 },
      { at: 1, piece: BONGO, level: 0.65 },
      { at: 1.5, piece: BONGO, level: 0.45 },
      { at: 2, piece: BONGO, level: 0.75 },
      { at: 2.5, piece: BONGO, level: 0.45 },
      { at: 3, piece: BONGO_LOW, level: 0.8 },
      { at: 3.5, piece: BONGO_LOW, level: 0.7 },
      ...eighths(SHAKER, 0.5, 0.35),
      { at: 0, piece: KICK, level: 0.55 },
    ],
  },
  {
    id: 'son',
    // A 2-3 son clave, the campana on the beat and a conga tumbao — the slap on 2, the open tones on
    // 4 and its &. Two bars because the clave is: its two halves are not the same bar twice.
    //
    // 2-3 rather than 3-2 because a chart doesn't say which, and 2-3 is where most of what gets sung
    // over a montuno sits. A chart that feels backwards over it is a case for a 3-2 groove beside this.
    cycle: TWO_BARS,
    hits: [
      { at: 1, piece: CLAVE, level: 0.85 },
      { at: 2, piece: CLAVE, level: 0.85 },
      { at: 4, piece: CLAVE, level: 0.85 },
      { at: 5.5, piece: CLAVE, level: 0.85 },
      { at: 7, piece: CLAVE, level: 0.85 },

      { at: 0, piece: COWBELL, level: 0.75 },
      { at: 1, piece: COWBELL, level: 0.55 },
      { at: 2, piece: COWBELL, level: 0.65 },
      { at: 3, piece: COWBELL, level: 0.55 },
      { at: 4, piece: COWBELL, level: 0.75 },
      { at: 5, piece: COWBELL, level: 0.55 },
      { at: 6, piece: COWBELL, level: 0.65 },
      { at: 7, piece: COWBELL, level: 0.55 },

      { at: 1, piece: SLAP, level: 0.8 },
      { at: 3, piece: CONGA, level: 0.8 },
      { at: 3.5, piece: CONGA, level: 0.75 },
      { at: 5, piece: SLAP, level: 0.8 },
      { at: 7, piece: CONGA, level: 0.8 },
      { at: 7.5, piece: CONGA, level: 0.75 },
    ],
  },
  {
    id: 'merengue',
    // The güira over a tambora: the hand on 1 and 3, the stick on the rim between, and an open tone
    // on the & of 4 that throws the bar into the next one. The soft kick on every beat is the modern
    // band's, and it is what holds all that sixteenth-note chatter to the floor.
    cycle: BAR,
    hits: [
      ...guira(),
      { at: 0, piece: TUMBA, level: 0.85 },
      { at: 0.5, piece: RIM, level: 0.5 },
      { at: 1, piece: RIM, level: 0.6 },
      { at: 1.5, piece: RIM, level: 0.5 },
      { at: 2, piece: TUMBA, level: 0.85 },
      { at: 2.5, piece: RIM, level: 0.5 },
      { at: 3, piece: RIM, level: 0.6 },
      { at: 3.5, piece: CONGA, level: 0.8 },
      { at: 0, piece: KICK, level: 0.6 },
      { at: 1, piece: KICK, level: 0.5 },
      { at: 2, piece: KICK, level: 0.6 },
      { at: 3, piece: KICK, level: 0.5 },
    ],
  },
  {
    id: 'charleston',
    // The kick doubles the figure — 1 and the & of 2 — and the hat keeps 2 and 4. Straight, since
    // nothing here swings yet.
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.85 },
      { at: 1, piece: HAT, level: 0.6 },
      { at: 1.5, piece: KICK, level: 0.7 },
      { at: 3, piece: HAT, level: 0.6 },
    ],
  },
  {
    id: 'arpeggio',
    // Barely there: a roll that piles up into the chord wants time kept under it, not a beat played
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.6 },
      { at: 0, piece: HAT, level: 0.4 },
      { at: 1, piece: HAT, level: 0.35 },
      { at: 2, piece: HAT, level: 0.4 },
      { at: 3, piece: HAT, level: 0.35 },
    ],
  },
  {
    id: 'balada',
    // A worship ballad's kit: the kick on 1 and the & of 3, a cross-stick on 2 and 4 rather than the
    // full snare, and the hat underneath it all at a whisper
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.85 },
      { at: 1, piece: RIM, level: 0.7 },
      { at: 2.5, piece: KICK, level: 0.7 },
      { at: 3, piece: RIM, level: 0.7 },
      ...eighths(HAT, 0.35, 0.25),
    ],
  },
  {
    id: 'adoracion',
    // The kick sits under the pattern's own chord hits, anticipation and all — the & of 4 that ties
    // into the second bar is why the second bar's downbeat has no kick of its own. The snare in half
    // time, on 3, is the build a worship set leans on.
    cycle: TWO_BARS,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1.5, piece: KICK, level: 0.7 },
      { at: 2, piece: SNARE, level: 0.75 },
      { at: 3.5, piece: KICK, level: 0.75 },
      { at: 5.5, piece: KICK, level: 0.7 },
      { at: 6, piece: SNARE, level: 0.75 },
      ...eighths(HAT, 0.4, 0.28, 8),
    ],
  },
  {
    id: 'suave',
    // No kit at all to speak of: a shaker leaning on the &s and a soft kick, for the pattern that is
    // one line over a pedal
    cycle: BAR,
    hits: [...eighths(SHAKER, 0.3, 0.45), { at: 0, piece: KICK, level: 0.55 }, { at: 2.5, piece: KICK, level: 0.45 }],
  },
  {
    id: 'pulso',
    // The worship build: a soft four-on-the-floor kick, the floor tom on 2 and 4 where a snare would
    // be, and the hats keeping the eighths the chord is pulsing in
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.75 },
      { at: 1, piece: KICK, level: 0.55 },
      { at: 1, piece: TOM, level: 0.55 },
      { at: 2, piece: KICK, level: 0.7 },
      { at: 3, piece: KICK, level: 0.55 },
      { at: 3, piece: TOM, level: 0.55 },
      ...eighths(HAT, 0.35, 0.25),
    ],
  },
  {
    id: 'rocio',
    // The kick under the figure's first two hits, a cross-stick on 3, and the ride on the beat
    // instead of the hats
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.8 },
      { at: 1.5, piece: KICK, level: 0.55 },
      { at: 2, piece: RIM, level: 0.6 },
      { at: 0, piece: RIDE, level: 0.4 },
      { at: 1, piece: RIDE, level: 0.3 },
      { at: 2, piece: RIDE, level: 0.35 },
      { at: 3, piece: RIDE, level: 0.3 },
    ],
  },
  {
    id: 'ofrenda',
    // Toms only, under a shaker on the beat: a kick on each downbeat, the floor tom answering on 3,
    // and a two-note walk into the turn at the end of the second bar
    cycle: TWO_BARS,
    hits: [
      { at: 0, piece: KICK, level: 0.65 },
      { at: 2, piece: TOM, level: 0.5 },
      { at: 3.5, piece: TOM, level: 0.35 },
      { at: 4, piece: KICK, level: 0.6 },
      { at: 6, piece: TOM, level: 0.5 },
      { at: 7, piece: TOM, level: 0.4 },
      { at: 7.5, piece: TOM, level: 0.45 },
      ...[0, 1, 2, 3, 4, 5, 6, 7].map((at) => ({ at, piece: SHAKER, level: 0.28 })),
    ],
  },
  {
    id: 'victoria',
    // Four-on-the-floor praise: the kick on every beat, the snare and the tambourine on 2 and 4, and
    // the open hat on every & lifting it
    cycle: BAR,
    hits: [
      ...[0, 1, 2, 3].map((at) => ({ at, piece: KICK, level: 0.85 })),
      { at: 1, piece: SNARE, level: 0.85 },
      { at: 3, piece: SNARE, level: 0.85 },
      { at: 1, piece: TAMBOURINE, level: 0.5 },
      { at: 3, piece: TAMBOURINE, level: 0.5 },
      ...[0, 1, 2, 3].map((at) => ({ at, piece: HAT, level: 0.4 })),
      ...[0.5, 1.5, 2.5, 3.5].map((at) => ({ at, piece: OPEN_HAT, level: 0.35 })),
    ],
  },
  {
    id: 'aclamacion',
    // A half-time anthem: the kick on 1 and the push off the & of 2, the snare on 3, the floor tom
    // rolling eighths under it all and the tambourine on 2 and 4
    cycle: TWO_BARS,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1.5, piece: KICK, level: 0.7 },
      { at: 2, piece: SNARE, level: 0.85 },
      { at: 4, piece: KICK, level: 0.9 },
      { at: 5.5, piece: KICK, level: 0.7 },
      { at: 6, piece: SNARE, level: 0.85 },
      { at: 6.5, piece: KICK, level: 0.6 },
      ...eighths(TOM, 0.5, 0.3, 8),
      ...[1, 3, 5, 7].map((at) => ({ at, piece: TAMBOURINE, level: 0.45 })),
    ],
  },
  {
    id: 'gloria',
    // Gospel praise: the kick locked to the bass's sixteenths, the snare on 2 and 4, and the
    // tambourine running sixteenths over the hats
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 0.75, piece: KICK, level: 0.6 },
      { at: 2, piece: KICK, level: 0.8 },
      { at: 2.75, piece: KICK, level: 0.65 },
      { at: 1, piece: SNARE, level: 0.85 },
      { at: 3, piece: SNARE, level: 0.85 },
      ...eighths(HAT, 0.45, 0.3),
      ...Array.from({ length: 16 }, (unused, index) => ({
        at: index / 4,
        piece: TAMBOURINE,
        level: [0.45, 0.22, 0.32, 0.22][index % 4],
      })),
    ],
  },
  {
    id: 'clamor',
    // The chorus a set builds to: the full snare in half time on 3, the kick on 1, the & of 2 and the
    // & of 4, and the ride driving eighths over it
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1.5, piece: KICK, level: 0.6 },
      { at: 2, piece: SNARE, level: 0.9 },
      { at: 3.5, piece: KICK, level: 0.55 },
      ...eighths(RIDE, 0.45, 0.3),
    ],
  },
  {
    id: 'corito',
    // The polka: the kick on every beat and the snare on every & between, nothing else
    cycle: BAR,
    hits: [
      ...[0, 1, 2, 3].map((at) => ({ at, piece: KICK, level: at % 2 === 0 ? 0.85 : 0.7 })),
      ...[0.5, 1.5, 2.5, 3.5].map((at) => ({ at, piece: SNARE, level: 0.6 })),
    ],
  },
  {
    id: 'brisa',
    // A one-drop: nothing on 1, the kick and the cross-stick together on 3, hats in eighths and an
    // open hat on the & of 4
    cycle: BAR,
    hits: [
      { at: 2, piece: KICK, level: 0.85 },
      { at: 2, piece: RIM, level: 0.75 },
      ...eighths(HAT, 0.45, 0.3, 3.5),
      { at: 3.5, piece: OPEN_HAT, level: 0.35 },
    ],
  },
  {
    id: 'altar',
    // 4/4 worship under block chords: half time, the kick on 1, a cross-stick on 3, and a soft kick on
    // the & of 4 leaning into the next downbeat
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.85 },
      { at: 2, piece: RIM, level: 0.6 },
      { at: 3.5, piece: KICK, level: 0.4 },
      ...eighths(HAT, 0.35, 0.22),
    ],
    levels: {
      // Still in half time, the snare on 3 only, so the chorus doesn't sound rushed: the kick adds
      // the & of 2, the ride takes the hat's eighths, a ghost note after the snare, the floor tom on 1 and 3
      [STRONG]: [
        { at: 0, piece: KICK, level: 0.95 },
        { at: 1.5, piece: KICK, level: 0.6 },
        { at: 3.5, piece: KICK, level: 0.55 },
        { at: 2, piece: SNARE, level: 0.85 },
        { at: 3.5, piece: SNARE, level: 0.22 },
        ...eighths(RIDE, 0.55, 0.35),
        { at: 0, piece: TOM, level: 0.35 },
        { at: 2, piece: TOM, level: 0.3 },
      ],
      // The big chorus, still in half time: a crash each bar, the tambourine with the snare, ghost
      // notes either side of it, the tom answering on the & of 3 and the hat opened into the next bar
      [FULL]: [
        { at: 0, piece: CRASH, level: 0.6 },
        { at: 0, piece: KICK, level: 1 },
        { at: 1.5, piece: KICK, level: 0.7 },
        { at: 3.5, piece: KICK, level: 0.6 },
        { at: 1.5, piece: SNARE, level: 0.2 },
        { at: 2, piece: SNARE, level: 0.95 },
        { at: 3.5, piece: SNARE, level: 0.28 },
        { at: 2, piece: TAMBOURINE, level: 0.55 },
        ...eighths(RIDE, 0.6, 0.4),
        { at: 0, piece: TOM, level: 0.45 },
        { at: 2, piece: TOM, level: 0.4 },
        { at: 2.5, piece: TOM, level: 0.3 },
        { at: 3.5, piece: OPEN_HAT, level: 0.4 },
      ],
    },
  },
  {
    id: 'celebra',
    // 4/4 praise under block chords: the full snare on 2 and 4, a kick pushing off the & of 2, and an
    // open hat on the & of 4 throwing the bar into the next one — so the closed hats stop short of it
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1, piece: SNARE, level: 0.85 },
      { at: 1.5, piece: KICK, level: 0.6 },
      { at: 2, piece: KICK, level: 0.85 },
      { at: 3, piece: SNARE, level: 0.85 },
      ...eighths(HAT, 0.55, 0.35, 3.5),
      { at: 3.5, piece: OPEN_HAT, level: 0.45 },
    ],
    levels: {
      // The ride in place of the hats, the tambourine on 2 and 4
      [STRONG]: [
        { at: 0, piece: KICK, level: 0.95 },
        { at: 1, piece: SNARE, level: 0.9 },
        { at: 1.5, piece: KICK, level: 0.7 },
        { at: 2, piece: KICK, level: 0.9 },
        { at: 3, piece: SNARE, level: 0.9 },
        ...eighths(RIDE, 0.55, 0.38, 3.5),
        { at: 3.5, piece: OPEN_HAT, level: 0.5 },
        { at: 1, piece: TAMBOURINE, level: 0.55 },
        { at: 3, piece: TAMBOURINE, level: 0.55 },
      ],
      // A crash on every bar, the kick busier, ghost notes leading into the snares, and the
      // tambourine in sixteenths
      [FULL]: [
        { at: 0, piece: CRASH, level: 0.65 },
        { at: 0, piece: KICK, level: 1 },
        { at: 1.5, piece: KICK, level: 0.75 },
        { at: 2, piece: KICK, level: 0.95 },
        { at: 2.75, piece: KICK, level: 0.55 },
        { at: 3.5, piece: KICK, level: 0.65 },
        { at: 1, piece: SNARE, level: 0.95 },
        { at: 1.75, piece: SNARE, level: 0.3 },
        { at: 3, piece: SNARE, level: 0.95 },
        { at: 3.75, piece: SNARE, level: 0.35 },
        ...eighths(RIDE, 0.6, 0.42, 3.5),
        { at: 3.5, piece: OPEN_HAT, level: 0.55 },
        ...Array.from({ length: 16 }, (unused, index) => ({
          at: index / 4,
          piece: TAMBOURINE,
          level: [0.5, 0.25, 0.35, 0.25][index % 4],
        })),
      ],
    },
  },
  {
    id: 'exalta',
    // Latin praise, from the drum intro of Canta al Señor: kick on 1 and its &, the snare on 2, then
    // the hat over soft ghost-note snares on the sixteenths up to the full snare on 4. As a phrase.
    cycle: PHRASE,
    hits: phrase(
      [
        { at: 0, piece: KICK, level: 0.9 },
        { at: 0.5, piece: KICK, level: 0.75 },
        { at: 1, piece: SNARE, level: 0.9 },
        { at: 1.75, piece: SNARE, level: 0.28 },
        { at: 2.25, piece: SNARE, level: 0.3 },
        { at: 2.75, piece: SNARE, level: 0.35 },
        { at: 3, piece: SNARE, level: 0.9 },
        { at: 0, piece: HAT, level: 0.5 },
        { at: 0.5, piece: HAT, level: 0.35 },
        { at: 1.5, piece: HAT, level: 0.6 },
        { at: 2, piece: HAT, level: 0.6 },
        { at: 2.5, piece: HAT, level: 0.6 },
        { at: 3, piece: HAT, level: 0.5 },
        { at: 3.5, piece: HAT, level: 0.5 },
      ],
      4,
    ),
  },
  {
    id: 'alabanza',
    // Exalta's kick-kick-snare twice a bar: the kick on 1 and 3 and each &, ghost notes leading into
    // both snares, and an open hat on the last & throwing the two bars round. As a phrase.
    cycle: PHRASE,
    hits: phrase(
      [
        ...[0, 4].flatMap((bar) => [
          { at: bar, piece: KICK, level: 0.9 },
          { at: bar + 0.5, piece: KICK, level: 0.7 },
          { at: bar + 0.75, piece: SNARE, level: 0.25 },
          { at: bar + 1, piece: SNARE, level: 0.9 },
          { at: bar + 1.75, piece: SNARE, level: 0.3 },
          { at: bar + 2, piece: KICK, level: 0.85 },
          { at: bar + 2.5, piece: KICK, level: 0.65 },
          { at: bar + 2.75, piece: SNARE, level: 0.3 },
          { at: bar + 3, piece: SNARE, level: 0.9 },
        ]),
        { at: 3.75, piece: SNARE, level: 0.3 },
        ...eighths(HAT, 0.55, 0.4, 7.5),
        { at: 7.5, piece: OPEN_HAT, level: 0.5 },
      ],
      8,
    ),
  },
  {
    id: 'hosanna',
    // Busier still: the hat in sixteenths, the kick on 1, its & and the & of 2, ghost notes on the e
    // and a of the beats around a full snare on 2 and 4, and the tambourine doubling the snare.
    // As a phrase.
    cycle: PHRASE,
    hits: phrase(
      [
        { at: 0, piece: KICK, level: 0.9 },
        { at: 0.5, piece: KICK, level: 0.7 },
        { at: 1, piece: SNARE, level: 0.9 },
        { at: 1.25, piece: SNARE, level: 0.25 },
        { at: 1.5, piece: KICK, level: 0.75 },
        { at: 2, piece: KICK, level: 0.6 },
        { at: 2.25, piece: SNARE, level: 0.3 },
        { at: 2.75, piece: SNARE, level: 0.35 },
        { at: 3, piece: SNARE, level: 0.9 },
        { at: 3.75, piece: SNARE, level: 0.3 },
        { at: 1, piece: TAMBOURINE, level: 0.45 },
        { at: 3, piece: TAMBOURINE, level: 0.45 },
        ...Array.from({ length: 16 }, (unused, index) => ({
          at: index / 4,
          piece: HAT,
          level: [0.5, 0.25, 0.4, 0.25][index % 4],
        })),
      ],
      4,
    ),
  },
  {
    id: 'maranata',
    // A four-bar phrase, after Cristo Viene: the kick on 1, the & of 2, 3 and the & of 4, the snare on
    // 2 and 4 under sixteenth hats, a crash opening the phrase and a sixteenth snare fill closing it
    cycle: 16,
    hits: [
      ...[0, 4, 8, 12].flatMap((bar) => [
        { at: bar, piece: KICK, level: 0.85 },
        { at: bar + 1, piece: SNARE, level: 0.85 },
        { at: bar + 1.5, piece: KICK, level: 0.7 },
        { at: bar + 2, piece: KICK, level: 0.85 },
        { at: bar + 3, piece: SNARE, level: 0.85 },
        { at: bar + 3.5, piece: KICK, level: 0.7 },
      ]),
      ...Array.from({ length: 64 }, (unused, index) => ({ at: index / 4, piece: HAT, level: index % 4 === 0 ? 0.5 : 0.4 })),
      { at: 0, piece: CRASH, level: 0.9 },
      ...[15, 15.25, 15.5, 15.75].map((at) => ({ at, piece: SNARE, level: 0.8 })),
    ],
  },
  {
    id: 'fanfarria',
    // Latin pop-rock under horn stabs: the kick on 1, the & of 2 and 3, the snare on 2 and 4, and an
    // open conga on the & of 1 and the & of 3. As a phrase.
    cycle: PHRASE,
    hits: phrase(
      [
        { at: 0, piece: KICK, level: 0.9 },
        { at: 1, piece: SNARE, level: 0.85 },
        { at: 1.5, piece: KICK, level: 0.7 },
        { at: 2, piece: KICK, level: 0.85 },
        { at: 3, piece: SNARE, level: 0.85 },
        { at: 0.5, piece: CONGA, level: 0.55 },
        { at: 2.5, piece: CONGA, level: 0.55 },
        ...eighths(HAT, 0.5, 0.35),
      ],
      4,
    ),
  },
  {
    id: 'presencia',
    // Four bars: Maranata's kick under sixteenth hats, open congas between the snares, and a timbale
    // and conga roll off the & of 2 in the last bar, with the snare under it, into the crash
    cycle: 16,
    hits: [
      ...[0, 4, 8].flatMap((bar) => [
        { at: bar, piece: KICK, level: 0.9 },
        { at: bar + 0.5, piece: CONGA, level: 0.6 },
        { at: bar + 1, piece: SNARE, level: 0.9 },
        { at: bar + 1.25, piece: CONGA, level: 0.55 },
        { at: bar + 1.5, piece: KICK, level: 0.75 },
        { at: bar + 2, piece: KICK, level: 0.85 },
        { at: bar + 2.5, piece: CONGA, level: 0.6 },
        { at: bar + 3, piece: SNARE, level: 0.9 },
        { at: bar + 3.25, piece: CONGA, level: 0.55 },
        { at: bar + 3.5, piece: KICK, level: 0.75 },
        { at: bar + 3.75, piece: SNARE, level: 0.55 },
      ]),
      // The hats stop where the roll starts
      ...Array.from({ length: 54 }, (unused, index) => ({ at: index / 4, piece: HAT, level: index % 4 === 0 ? 0.5 : 0.35 })),
      { at: 12, piece: KICK, level: 0.9 },
      { at: 13, piece: KICK, level: 0.85 },
      { at: 14, piece: KICK, level: 0.9 },
      ...Array.from({ length: 10 }, (unused, index) => [
        { at: 13.5 + index / 4, piece: index % 2 === 0 ? TIMBALE : CONGA, level: 0.6 + index * 0.035 },
        { at: 13.5 + index / 4, piece: SNARE, level: 0.4 + index * 0.03 },
      ]).flat(),
      { at: 0, piece: CRASH, level: 0.9 },
    ],
  },
  {
    id: 'regocijo',
    // Latin praise at about 125 BPM: the kick on every beat and pushing off the & of 4, the snare on
    // 2 and 4 with a pickup on the last sixteenth, and sixteenth hats leaning on the &. As a phrase.
    cycle: PHRASE,
    hits: phrase(
      [
        ...[0, 1, 2, 3].map((at) => ({ at, piece: KICK, level: 0.9 })),
        { at: 3.5, piece: KICK, level: 0.75 },
        { at: 1, piece: SNARE, level: 0.85 },
        { at: 3, piece: SNARE, level: 0.85 },
        { at: 3.75, piece: SNARE, level: 0.55 },
        ...Array.from({ length: 16 }, (unused, index) => ({
          at: index / 4,
          piece: HAT,
          level: [0.3, 0.25, 0.5, 0.25][index % 4],
        })),
      ],
      4,
    ),
  },
  {
    id: 'exodo',
    // Latin praise at 120 BPM: Maranata's kick, the snare on 2 and 4 with a pickup on the last
    // sixteenth, sixteenth hats, congas between, and a timbale on the a of 2 and of 4. As a phrase.
    cycle: PHRASE,
    hits: phrase(
      [
        { at: 0, piece: KICK, level: 0.9 },
        { at: 1.5, piece: KICK, level: 0.75 },
        { at: 2, piece: KICK, level: 0.85 },
        { at: 3.5, piece: KICK, level: 0.75 },
        { at: 1, piece: SNARE, level: 0.9 },
        { at: 3, piece: SNARE, level: 0.9 },
        { at: 3.75, piece: SNARE, level: 0.55 },
        ...[0.5, 1.25, 2.5, 3.25].map((at) => ({ at, piece: CONGA, level: 0.6 })),
        { at: 1.75, piece: TIMBALE, level: 0.55 },
        { at: 3.75, piece: TIMBALE, level: 0.6 },
        ...Array.from({ length: 16 }, (unused, index) => ({
          at: index / 4,
          piece: HAT,
          level: index % 4 === 0 ? 0.5 : 0.35,
        })),
      ],
      4,
    ),
  },
  // The grooves below are written in their own bar, not cut down from 4/4: three beats, or six
  // eighths for 6/8.
  {
    id: 'himno',
    // A 3/4 worship ballad: the kick on 1, a cross-stick on 3, and a soft kick on its & leaning
    // into the next downbeat
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.85 },
      { at: 2, piece: RIM, level: 0.6 },
      { at: 2.5, piece: KICK, level: 0.4 },
      ...eighths(HAT, 0.35, 0.22, 3),
    ],
  },
  {
    id: 'jubilo',
    // 3/4 praise: the full snare on 2 and 3, a kick pushing off the & of 2, and an open hat on the
    // & of 3 throwing the bar into the next one — so the closed hats stop short of it
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 1, piece: SNARE, level: 0.8 },
      { at: 1.5, piece: KICK, level: 0.6 },
      { at: 2, piece: SNARE, level: 0.7 },
      ...eighths(HAT, 0.55, 0.35, 2.5),
      { at: 2.5, piece: OPEN_HAT, level: 0.4 },
    ],
  },
  {
    id: 'mecida',
    // A 6/8 worship ballad, in eighths: the kick on 1, a cross-stick on 4, a soft kick on 6 into the
    // next bar, and the hats rocking in threes
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.85 },
      { at: 3, piece: RIM, level: 0.65 },
      { at: 5, piece: KICK, level: 0.35 },
      ...inThrees(HAT, 0.4, 0.25),
    ],
  },
  {
    id: 'gozo',
    // 6/8 praise: kicks on 1, 3 and 6 around a full snare on 4. Two bars so the second can trade its
    // last kick for a snare pickup into the turn.
    cycle: TWO_BARS,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 2, piece: KICK, level: 0.6 },
      { at: 3, piece: SNARE, level: 0.85 },
      { at: 5, piece: KICK, level: 0.65 },
      { at: 6, piece: KICK, level: 0.9 },
      { at: 8, piece: KICK, level: 0.6 },
      { at: 9, piece: SNARE, level: 0.85 },
      { at: 11, piece: SNARE, level: 0.55 },
      ...inThrees(HAT, 0.55, 0.35, 12),
    ],
  },
  {
    id: 'reposo',
    // 3/4 worship under a rolled chord: the kick on 1, a soft floor tom on 3, a shaker in eighths
    // and the ride marking the downbeat
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.7 },
      { at: 0, piece: RIDE, level: 0.35 },
      { at: 2, piece: TOM, level: 0.4 },
      ...eighths(SHAKER, 0.3, 0.2, 3),
    ],
  },
  {
    id: 'quietud',
    // 6/8 worship, in eighths: the kick on 1, the floor tom on 4, and the ride rocking in threes
    cycle: BAR,
    hits: [{ at: 0, piece: KICK, level: 0.7 }, { at: 3, piece: TOM, level: 0.5 }, ...inThrees(RIDE, 0.4, 0.22)],
  },
  {
    id: 'pandero',
    // 6/8 praise, in eighths: the kick on each half of the bar, the snare on the last eighth of each,
    // and the tambourine on every eighth, leaning on 1 and 4
    cycle: BAR,
    hits: [
      { at: 0, piece: KICK, level: 0.9 },
      { at: 2, piece: SNARE, level: 0.5 },
      { at: 3, piece: KICK, level: 0.8 },
      { at: 5, piece: SNARE, level: 0.6 },
      ...inThrees(TAMBOURINE, 0.5, 0.3),
    ],
  },
];

/**
 * The groove under block chords: the song's category picks worship or praise in 4/4, 3/4 and 6/8,
 * and a song with none gets the worship one. Every other meter gets the plain backbeat.
 */
export const getBlockGroove = (beatsPerBar, category = '') => getBlockStyle(beatsPerBar, category)?.drums || 'pop';

/** One groove by id, or null — a style with no drums of its own simply plays without them. */
export const getDrumGroove = (id) => DRUM_GROOVES.find((groove) => groove.id === id) || null;

/**
 * A groove laid out across a whole chart: every hit, in beats from the top of it.
 *
 * Counted from the first bar line and straight through, whatever the chords are doing — see the note
 * at the top of this file. A hit past the end of its own cycle belongs to a longer bar than this one
 * and is dropped, the same rule getPatternSteps follows, so a groove written in 4/4 loses its fourth
 * beat in 3/4 rather than spilling into the bar after.
 *
 * Two hits of the same piece on the same beat come back as one, the louder. Every piece in the kit is
 * a monophonic voice, and striking one twice at the same instant is not a louder note but an error.
 *
 * @param {object} groove - One of DRUM_GROOVES
 * @param {object} span - { bars, beatsPerBar }
 * @returns {Array<{at, piece, level, hold}>} ascending by beat; `hold` is null for a plain strike
 */
export const getGrooveHits = (groove, { bars, beatsPerBar = 4 } = {}) => {
  const perBar = Number(beatsPerBar);
  const cycle = getCycleBeats(groove?.cycle, perBar);
  const total = bars * perBar;

  if (!(cycle > 0) || !(total > 0)) return [];

  const hits = new Map();

  for (let start = 0; start < total - BEAT_EPSILON; start += cycle) {
    groove.hits.forEach(({ at, piece, level = 1, hold = null }) => {
      if (at >= cycle - BEAT_EPSILON) return;

      const beat = start + at;

      if (beat >= total - BEAT_EPSILON) return;

      const key = `${piece}|${beat}`;
      const existing = hits.get(key);

      if (!existing || level > existing.level) hits.set(key, { at: beat, piece, level, hold });
    });
  }

  return [...hits.values()].sort((a, b) => a.at - b.at);
};

// Two hits of the same piece on the same beat as one, the louder — see getGrooveHits
const mergeHits = (hits) => {
  const merged = new Map();

  hits.forEach((hit) => {
    const key = `${hit.piece}|${hit.at}`;
    const existing = merged.get(key);

    if (!existing || hit.level > existing.level) merged.set(key, hit);
  });

  return [...merged.values()].sort((a, b) => a.at - b.at);
};

/**
 * What the drums do at each intensity level out of chartIntensity.js. Sparse is no drums at all.
 * Soft keeps the groove's shape but takes the backbeat to the cross-stick and drops the ghost notes,
 * the crash and the tambourine. Strong moves the hats on the eighths to the ride (`ride`, its level
 * lift), the cross-stick to the snare, and puts the floor tom on the pulse (`floorTom`). Full adds a
 * crash on every bar line, a tambourine on the backbeat, and opens the hat into each bar.
 */
const INTENSITY_DRUMS = {
  1: null,
  2: { scale: 0.7, swap: { [SNARE]: RIM, [OPEN_HAT]: HAT }, drop: [CRASH, TAMBOURINE], ghost: 0.4 },
  3: { scale: 1 },
  4: { scale: 1.1, ride: 1.3, snare: true, floorTom: 0.3 },
  5: {
    scale: 1.15,
    ride: 1.3,
    snare: true,
    floorTom: 0.3,
    crashEveryBar: 0.6,
    backbeat: TAMBOURINE,
    openLastAnd: true,
  },
};

// A drum kit rather than hand percussion: a kick under hats, a ride or a snare. Only a kit is
// orchestrated by level; a son or a merengue keeps its own pieces.
const isKit = (hits) =>
  hits.some(({ piece }) => piece === KICK) && hits.some(({ piece }) => [HAT, OPEN_HAT, RIDE, SNARE].includes(piece));

const isOnEighth = (at) => Math.abs(at * 2 - Math.round(at * 2)) < BEAT_EPSILON;

/**
 * A segment's groove hits played at an intensity level. Normal, or no level, leaves them as they are.
 *
 * @param {Array<{at, piece, level, hold}>} hits - As getGrooveHits lays them out
 * @param {number} intensity - 1 to 5
 * @param {object} span - { bars, beatsPerBar }
 */
export const applyIntensity = (hits, intensity, { bars, beatsPerBar = 4 } = {}) => {
  const rule = INTENSITY_DRUMS[intensity] === undefined ? INTENSITY_DRUMS[3] : INTENSITY_DRUMS[intensity];

  if (!rule) return [];
  if (rule.scale === 1 && !rule.backbeat) return hits;

  const perBar = Number(beatsPerBar);
  const lastAnd = perBar - 0.5;
  const kit = isKit(hits);
  const has = (piece) => hits.some((hit) => hit.piece === piece);
  // A groove kept on the cross-stick comes off it onto the snare
  const swap = kit && rule.snare && !has(SNARE) ? { ...rule.swap, [RIM]: SNARE } : rule.swap;
  const played = hits
    .filter(({ piece, level }) => !rule.drop?.includes(piece) && !(rule.ghost && piece === SNARE && level < rule.ghost))
    .map((hit) => {
      const opens = rule.openLastAnd && hit.piece === HAT && Math.abs((hit.at % perBar) - lastAnd) < BEAT_EPSILON;
      // The sixteenths between stay on the hat, ticking under the ride
      const rides = !opens && kit && rule.ride && hit.piece === HAT && isOnEighth(hit.at);
      const piece = opens ? OPEN_HAT : rides ? RIDE : swap?.[hit.piece] || hit.piece;

      return { ...hit, piece, level: Math.min(1, hit.level * rule.scale * (rides ? rule.ride : 1)) };
    });

  // The floor tom on every beat, or every dotted quarter in 6/8, where the groove has none of its own
  const pulse = perBar === 6 ? 3 : 1;
  const floorTom =
    kit && rule.floorTom && !has(TOM)
      ? Array.from({ length: Math.floor((bars * perBar) / pulse) || 0 }, (unused, index) => ({
          at: index * pulse,
          piece: TOM,
          level: rule.floorTom,
          hold: null,
        }))
      : [];
  const crashes =
    kit && rule.crashEveryBar
      ? Array.from({ length: bars || 0 }, (unused, index) => ({
          at: index * perBar,
          piece: CRASH,
          level: rule.crashEveryBar,
          hold: null,
        }))
      : [];

  // The tambourine on 2 and 4, only in 4/4 where that is the backbeat
  const backbeat =
    rule.backbeat && perBar === 4 && !hits.some(({ piece }) => piece === rule.backbeat)
      ? Array.from({ length: bars * 2 }, (unused, index) => ({
          at: Math.floor(index / 2) * perBar + (index % 2 === 0 ? 1 : 3),
          piece: rule.backbeat,
          level: 0.6,
          hold: null,
        }))
      : [];

  return mergeHits([...played, ...floorTom, ...crashes, ...backbeat]);
};

/**
 * Where a part changes level, what the drums play across the line. Going up, a fill closes the part
 * before and a crash opens the next: a snare fill where the groove has a snare, else the floor tom.
 * A build, up two levels or into Full, fills the whole last bar, eighths doubling to sixteenths
 * halfway, with the kick on the pulse and a swell rising into the crash. Going down, the drums pull
 * back and leave the end of the part before empty; into Sparse, a kit rings out on a crash there
 * instead. Same level, nothing. A drop then silences the last beat, and the next comes back on a crash.
 *
 * @param {object} from - The segment ending: `{ hits, intensity, bars }`, hits as applyIntensity gives them
 * @param {object} to - The segment starting: `{ intensity, fill, drop }`. `fill: false` leaves the line
 *   alone; `drop` still applies
 * @param {number} beatsPerBar
 * @returns {{ from: object[], crash: boolean }} The ending segment's hits redone, and whether the next opens on a crash
 */
export const getTransitionHits = (from, to, beatsPerBar = 4) => {
  const line = getLineHits(from, to, beatsPerBar);
  const silence = from.bars * Number(beatsPerBar) - getDropBeats(beatsPerBar);

  if (!to.drop || !(silence > 0)) return line;

  // Nothing struck across the drop, and a held hit, such as a swell, let go where it starts
  const kept = line.from
    .filter(({ at }) => at < silence - BEAT_EPSILON)
    .map((hit) => (hit.hold && hit.at + hit.hold > silence ? { ...hit, hold: silence - hit.at } : hit));

  return { from: kept, crash: true };
};

const getLineHits = (from, to, beatsPerBar) => {
  const perBar = Number(beatsPerBar);
  const before = from.intensity || 3;
  const after = to.intensity || 3;
  const total = from.bars * perBar;

  if (to.fill === false || before === after || !(total > 0)) return { from: from.hits, crash: false };

  // A beat of sixteenths in 4/4 and 3/4; half a bar of sixteenths in 6/8, where a beat is an eighth
  const compound = perBar === 6;
  const length = (compound ? 3 : 1) * (Math.abs(after - before) >= 2 ? 2 : 1);

  if (after < before) {
    const start = Math.max(0, total - length);
    const kept = from.hits.filter(({ at }) => at < start - BEAT_EPSILON);

    if (after > 1 || !isKit(from.hits)) return { from: kept, crash: false };

    // Into Sparse a kit stops on a crash and lets it ring a bar on over the line, rather than just stopping
    const ringOut = [
      { at: start, piece: CRASH, level: 0.7, hold: total - start + perBar },
      { at: start, piece: KICK, level: 0.8, hold: null },
    ];

    return { from: mergeHits([...kept, ...ringOut]), crash: false };
  }

  const build = after - before >= 2 || after === 5;
  const barStart = Math.max(0, total - perBar);
  const swell = build ? [{ at: barStart, piece: SWELL, level: 0.8, hold: total - barStart }] : [];

  // Out of Sparse there are no drums to fill with; they come in on the crash
  if (before === 1) return { from: mergeHits([...from.hits, ...swell]), crash: true };

  const start = build ? barStart : Math.max(0, total - length);
  const piece = from.hits.some((hit) => hit.piece === SNARE || hit.piece === RIM) ? SNARE : TOM;
  const step = piece === SNARE ? (compound ? 0.5 : 0.25) : 0.5;
  const half = build ? start + (total - start) / 2 : start;
  const strokes = (first, end, every) =>
    Array.from({ length: Math.round((end - first) / every) }, (unused, index) => first + index * every);
  const ats = [...strokes(start, half, step * 2), ...strokes(half, total, step)];
  const fill = ats.map((at, index) => ({
    at,
    piece,
    level: 0.55 + (0.4 * index) / Math.max(1, ats.length - 1),
    hold: null,
  }));
  // The kick on every beat of a build's last bar, or every dotted quarter in 6/8
  const pulse = compound ? 3 : 1;
  const kicks =
    build && from.hits.some((hit) => hit.piece === KICK)
      ? strokes(start, total, pulse).map((at) => ({ at, piece: KICK, level: 0.8, hold: null }))
      : [];
  // The hands are on the fill: the hats, the ride and the cross-stick make room for it
  const kept = from.hits.filter(
    ({ at, piece: struck }) => at < start - BEAT_EPSILON || ![HAT, OPEN_HAT, RIDE, RIM, SNARE].includes(struck),
  );

  return { from: mergeHits([...kept, ...fill, ...kicks, ...swell]), crash: true };
};

/**
 * A groove across `bars` at a level: its own bar for that level where it has one (`levels`), played
 * as written, else its bar through applyIntensity.
 */
export const getLevelHits = (groove, intensity, bars, beatsPerBar = 4) => {
  const own = groove.levels?.[intensity];

  if (own) return getGrooveHits({ ...groove, hits: own }, { bars, beatsPerBar });

  return applyIntensity(getGrooveHits(groove, { bars, beatsPerBar }), intensity, { bars, beatsPerBar });
};

/**
 * Every segment's drums for a run, in each segment's own beats: its groove at its level, the fills
 * and pull-backs where the level changes, and a crash opening a run that starts Strong or louder.
 *
 * @param {object[]} segments - `{ drumGroove, bars, intensity, fill, drums }`, `drums` the part's switch
 * @param {number} beatsPerBar
 * @returns {Array<object[]>} One list of hits per segment
 */
export const getRunDrumHits = (segments, beatsPerBar = 4) => {
  const laid = segments.map((segment) => {
    // Switched off, a part has no groove to fill into or out of; switched on, Sparse plays Soft's
    const drumGroove = segment.drums === LAYER_OFF ? null : segment.drumGroove;
    const intensity = segment.drums === LAYER_ON ? Math.max(SOFT, segment.intensity || NORMAL) : segment.intensity;

    return {
      ...segment,
      drumGroove,
      intensity,
      hits: drumGroove ? getLevelHits(drumGroove, intensity, segment.bars, beatsPerBar) : [],
    };
  });
  const crashes = laid.map((segment, index) => index === 0 && (segment.intensity || 3) >= 4);

  laid.forEach((segment, index) => {
    const next = laid[index + 1];

    if (!next || !segment.drumGroove || !next.drumGroove) return;

    const { from, crash } = getTransitionHits(segment, next, beatsPerBar);

    segment.hits = from;
    crashes[index + 1] = crash;
  });

  // The hat makes room for the crash, as in phrase()
  const crashed = (hits) => [
    ...hits.filter(({ at, piece }) => !(at === 0 && [HAT, OPEN_HAT].includes(piece))),
    { at: 0, piece: CRASH, level: 0.9, hold: null },
  ];

  return laid.map(({ hits }, index) => (crashes[index] && hits.length > 0 ? mergeHits(crashed(hits)) : hits));
};
